interface DeferrableAsyncCall<T extends any[], R> {
    args: T;
    resolve: (result?: R) => void;
    reject: (error: any) => void;
}

/**
 * Debounce executions of an async event handler function.
 *
 * When processing events, typically only the most recent event matters. When multiple events pile up, only the most
 * recent is processed.
 *
 * Only one event is processed at a time. Event handlers are expected to return a promise to indicate that they are
 * finished and ready to process the next event.
 *
 * "Debounced" calls which are never processed still return promises which resolve or reject, but they do not receive
 * the returned value of the call they were debounced for (they resolve to `undefiend` instead), nor do they receive
 * the original error thrown by the debounced call (they receive a custom event instead).
 */
export function debounceUsingPromises<T extends any[], R>(asyncEventHandler: (...args: T) => Promise<R>): (...args: T) => Promise<R | undefined> {
    let currentHandlerPromise: Promise<R> | undefined;
    const queuedEvents: Array<DeferrableAsyncCall<T, R>> = [];

    function handleCall(call: DeferrableAsyncCall<T, R>) {
        // Defer if there's already an event being handled
        if (currentHandlerPromise) {
            queuedEvents.push(call);
            return;
        }

        currentHandlerPromise = asyncEventHandler(...call.args);

        const debouncedCalls = queuedEvents.splice(0);
        for (const { resolve, reject } of debouncedCalls) {
            currentHandlerPromise.then(
                // Do not call resolve or reject debounced calls with the "actual" return values;
                // it would confuse people trying to track down the source of an error if one function call
                // returned the result of another.
                () => resolve(),
                () => reject(new Error('Event handler was debounced, but the handler this event was debounced for threw an error')),
            );
        }

        // This bit comes after the debounced call handlers because calls should be resolved in the order they were made.
        currentHandlerPromise.then(call.resolve, call.reject);

        currentHandlerPromise.finally(() => {
            currentHandlerPromise = undefined;

            // Collect the next event to process.
            const mostRecentCall = queuedEvents.pop();
            if (!mostRecentCall) {
                return;
            }

            // The rest of the queue will be collected inside of this call.

            return handleCall(mostRecentCall);
        });
    }

    return (...args: T) => {
        return new Promise<R | undefined>((resolve, reject) => {
            handleCall({ args, resolve, reject });
        });
    };
}
